import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import isEmpty from 'lodash-es/isEmpty';
import { catchError, exhaustMap, finalize, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { ApiHeaders } from '../../../models/api-query.model';
import { Invoice, InvoiceTotalAmountDue } from '../../../models/invoice-model';
import { AppFacadeService } from '../../../services/app-facade.service';
import { ConsumerPortalApiService } from '../../../services/consumer-portal-api.service';
import { InvoicesService } from '../../../services/invoices.service';
import {
    clearInvoicesinStore,
    failLoadInvoiceDetails,
    fetchInvoiceDetail,
    fetchInvoices,
    fetchInvoiceTotalDue,
    loadInvoicesAction,
    setInvoiceDetailData,
    setInvoicesLoading,
    setInvoiceTotalDue,
    setNoMoreRecordInvoices
} from './invoices.actions';
import { getPageNumberInvoices } from './invoices.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { SubComponentsToLoad } from '../../../enums/components-to-load.enum';
import { setSubActiveTab } from '../../app-wide/app/app.actions';

@Injectable()
export class InvoicesEffects {
    private readonly actions$ = inject(Actions);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly invoicesService = inject(InvoicesService);

    fetchInvoices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchInvoices),
            tap(() => this.appFacadeService.setLoading(true)),
            withLatestFrom(this.store.pipe(select(getPageNumberInvoices)), this.appFacadeService.getPageSizeValues()),
            exhaustMap(([action, currentPageNumber, pageSizeValues]) => {
                const { selectedPageSize, previousPageSize } = pageSizeValues;
                const { customerId, startDate, endDate, search, status } = action;
                let pageNumber = currentPageNumber;
                if (previousPageSize !== selectedPageSize) {
                    this.store.dispatch(clearInvoicesinStore());
                    pageNumber = 1;
                }
                return this.getInvoicesRecords(customerId, pageNumber, selectedPageSize, startDate, endDate, search, status).pipe(
                    switchMap(items => {
                        if (items.length <= 0) {
                            return of(setNoMoreRecordInvoices({ record: true }));
                        }

                        // Emit all accumulated actions
                        return of(loadInvoicesAction({ invoices: items }), setNoMoreRecordInvoices({ record: false }));
                    }),
                    catchError(() => {
                        // Handle the error by dispatching relevant actions
                        return of(setNoMoreRecordInvoices({ record: true }), setInvoicesLoading({ loading: false }));
                    }),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    // ADO-116163: resolve the "Total Due" header through the store so a store-driven refresh
    // (the post-payment invoice-list refresh in payment.effects) can refresh it too. switchMap so a
    // later refresh supersedes any in-flight total fetch.
    fetchInvoiceTotalDue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchInvoiceTotalDue),
            switchMap(({ customerId }) =>
                this.invoicesService.getInvoiceTotalAmountDue(customerId).pipe(
                    map((data: InvoiceTotalAmountDue[]) => setInvoiceTotalDue({ totalDue: data.length ? data[0]?.TotalAmountDue : undefined })),
                    catchError(() => of(setInvoiceTotalDue({ totalDue: undefined })))
                )
            )
        )
    );

    fetchInvoiceDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchInvoiceDetail),
            tap(() => this.appFacadeService.setLoading(true)),

            switchMap(({ customerId, invoiceId }) => {
                if (!customerId || !invoiceId) {
                    return of(failLoadInvoiceDetails({ error: 'Missing customerId or invoiceId' }));
                }
                const url = `customer/${customerId}/invoices/${invoiceId}`;
                const headers: ApiHeaders = {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    'x-columns': JSON.stringify(['Id', 'Name', 'LineItems', 'AdditionalFields', 'GrandTotal', 'TotalTax', 'GrandTaxTotal', 'InvoiceDate', 'Type'])
                };
                return this.consumerPortalApi.get<Invoice>(url, { headers: headers }).pipe(
                    mergeMap((detail: Invoice[]) => {
                        const data = detail as unknown as Invoice;
                        return [setInvoiceDetailData({ data }), setSubActiveTab({ subActiveTab: SubComponentsToLoad.InvoiceDetail })] as Action[];
                    }),
                    catchError((error: HttpErrorResponse) => of(failLoadInvoiceDetails({ error: error?.message }))),
                    finalize(() => this.appFacadeService.setLoading(false))
                );
            })
        )
    );

    getInvoicesRecords(customerId: number | string, pageNumber = 1, pageSize = 25, startDate?: string | undefined, endDate?: string | undefined, search?: string | undefined, status?: string) {
        const url = `customer/${customerId}/invoices?`;
        let headers: ApiHeaders = {};
        if (pageNumber && customerId) {
            headers = {
                /* eslint-disable @typescript-eslint/naming-convention */
                /**
                 * Note camelCase : DB Model
                 */
                ...headers,
                'x-paging': JSON.stringify({
                    page: pageNumber,
                    pageSize
                })
            };
        }
        const filters = [];
        if (startDate) {
            filters.push({ field: 'InvoiceDate', type: '>=', value: startDate + ' 00:00:01' });
        }
        if (endDate) {
            filters.push({ field: 'InvoiceDate', type: '<=', value: endDate + ' 11:59:59' });
        }
        if (search && !isEmpty(search)) {
            const isSyrinx = this.appFacadeService.featureToggleService.isSyrinx();
            const searchFilters = [];

            if (isSyrinx) {
                searchFilters.push(
                    { field: 'DepotName', type: 'LIKE', value: search },
                    { field: 'InvoiceNumber', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.Name', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.Line1', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.Line2', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.City', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.Country', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.Address.PostalCode', type: 'LIKE', value: search }
                );
            } else {
                searchFilters.push({ field: 'InvoiceNumber', type: 'LIKE', value: search });
            }
            filters.push({ field: '', type: 'OR', value: searchFilters });
        }
        if (status === 'Paid') {
            filters.push({ field: 'AmountDue', type: '=', value: 0 });
        } else if (status === 'Unpaid') {
            filters.push({ field: 'AmountDue', type: '!=', value: 0 });
        } else if (status === 'All') {
            // All = every invoice, including credits/negative-balance (AmountDue < 0), which also show
            // under Unpaid (!= 0). A nested OR of >= 0 and <= 0 is a tautology over all AmountDue, so it
            // matches credits too; the nested AmountDue field still trips the API's hasAmountDueFilter
            // guard so the default "AmountDue > 0" is suppressed (see customer.service.getCustomerInvoices).
            filters.push({
                field: '',
                type: 'OR',
                value: [
                    { field: 'AmountDue', type: '>=', value: 0 },
                    { field: 'AmountDue', type: '<=', value: 0 }
                ]
            });
        }
        if (filters.length > 0) {
            headers = {
                ...headers,
                'x-filter': JSON.stringify(filters)
            };
        }
        return this.consumerPortalApi.get<Invoice>(url, { headers: headers });
    }
}
